<template>
  <v-col
    v-if="enabled"
    :cols="cols"
    :lg="lg"
    :md="md"
  >
    <v-row dense>
      <v-col class="labeled-input">
        {{ $bct(message) }}
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="4">
        <v-text-field
          v-model.number="months"
          @update:model-value="combine"
          :append-inner-icon="locked ? 'lock' : ''"
          :aria-label="$bct('age')"
          :density="dense ? 'compact' : undefined"
          :disabled="locked"
          maxlength="2"
          min="0"
          type="number"
          variant="filled"
          hide-details
          round
          tile
        />
      </v-col>
      <v-col cols="8">
        <v-select
          v-model="period"
          @update:model-value="combine"
          :append-inner-icon="locked ? 'lock' : ''"
          :aria-label="$bct('months or years')"
          :density="dense ? 'compact' : undefined"
          :disabled="locked"
          :item-title="(item) => $bct(item.text)"
          :items="[
            { text: 'months', value: 'months' },
            { text: 'years', value: 'years' },
          ]"
          variant="filled"
          hide-details
          tile
        />
      </v-col>
    </v-row>
  </v-col>
</template>

<script setup>
import useLabeledField from '@/shared/composables/useLabeledField';
import useSchematizedField from '@/shared/composables/useSchematizedField';
import propsToRefs from '@/shared/utils/propsToRefs';

const $bct = inject('translator');

defineEmits(useLabeledField.emits);

const props = defineProps({
  ...useLabeledField.props,
  ...useSchematizedField.props,
});

const localValue = defineModel({ type: [String, Number] });

const months = ref(splitMonths(localValue.value));
const period = ref(splitYears(localValue.value));

const { handleBlur, handleUpdateModelValue } = useLabeledField(
  ...propsToRefs(props, useLabeledField.paramKeys),
);

const { enabled, locked } = useSchematizedField(
  ...propsToRefs(props, useSchematizedField.paramKeys),
);

watch(localValue, (newVal) => {
  if (newVal) {
    if (period.value === 'years' && months.value * 12 !== newVal) {
      period.value = 'months';
      months.value = newVal;
    } else if (period.value === 'months' && months.value !== newVal) {
      period.value = 'months';
      months.value = newVal;
    }
  } else {
    period.value = 'months';
    months.value = newVal;
  }
});

function combine() {
  let newVal;
  if (period.value === 'years') {
    newVal = months.value * 12;
  } else {
    newVal = months.value;
  }
  handleUpdateModelValue(newVal);
  handleBlur(newVal);
}

function splitMonths(val) {
  return val % 12 === 0 && val > 24 ? val / 12 : val;
}

function splitYears(val) {
  return val % 12 === 0 && val > 24 ? 'years' : 'months';
}
</script>
